<script setup lang="ts">
import { computed } from 'vue';
import type { StatusSnapshot } from '@/types';
import { statusHeadline, statusToneClass, formatRelativeTime } from '@/utils/status';
import { statusMessages } from '@/i18n';

const props = defineProps<{
  snapshot: StatusSnapshot | null;
  loading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
}>();

defineEmits<{
  refresh: [];
}>();

const headline = computed(() => {
  if (props.error) return statusMessages.statusUnavailable;
  if (!props.snapshot) return statusMessages.loading;
  return statusHeadline(props.snapshot.overall);
});

const toneClass = computed(() => {
  if (props.error) return 'tone-muted';
  if (!props.snapshot) return 'tone-muted';
  return statusToneClass(props.snapshot.overall);
});

const relative = computed(() => formatRelativeTime(props.lastUpdated));
</script>

<template>
  <section class="status-banner" :class="toneClass">
    <div class="banner-text">
      <span class="banner-dot" aria-hidden="true"></span>
      <span class="banner-headline">{{ headline }}</span>
    </div>
    <div class="banner-meta">
      <span class="banner-time">{{ statusMessages.updated(relative) }}</span>
      <button type="button" class="banner-refresh" :disabled="loading" @click="$emit('refresh')">
        {{ loading ? statusMessages.refreshing : statusMessages.refresh }}
      </button>
    </div>
  </section>
</template>

<style scoped>
.status-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid var(--xt-border);
  background: var(--xt-bg-elevated);
}

.banner-text {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.banner-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--xt-tone-fg);
  box-shadow: 0 0 0 4px var(--xt-tone-halo);
}

.banner-headline {
  font-weight: 600;
  font-size: 1rem;
  color: var(--xt-tone-fg);
}

.banner-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.banner-time {
  font-size: 0.85rem;
  color: var(--xt-fg-muted);
}

.banner-refresh {
  font-size: 0.85rem;
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid var(--xt-border);
  background: var(--xt-bg-base);
  color: var(--xt-fg-strong);
  cursor: pointer;
}

.banner-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
